import React, { useId, useState } from 'react';
import { FaLock, FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import MemoryClipPlayer from '../MemoryClipPlayer.jsx';
import '../../../styles/JustPlayedCard.css';

/** Covers shown in the Premium card's strip before "+N". */
const STRIP = 3;

/**
 * "Hotel California just played" (mockup 15, ENG-695): shown only to the
 * listener whose request just played, in the performer card's place, with
 * "Jump to my song" and, for a listener without Premium, the SONGSHAKE
 * PREMIUM card: their name, their memories' covers and "All N Memories,
 * organized in one place."
 *
 * Jump to my song plays the song's replay clip (ENG-758) in the card itself,
 * never over the broadcast: MemoryClipPlayer asks for the clip and shows the
 * player, "still being made", "no replay", or the Premium message for a
 * listener without it.
 */
const JustPlayedCard = ({ played, summary, onGoPremium, onDismiss }) => {
  const { t, format } = useLocale();
  const headingId = useId();
  const [jumped, setJumped] = useState(false);
  if (!played) return null;
  const premium = Boolean(summary?.premium);
  const covers = (summary?.recentArtwork ?? []).map(resolveMediaUrl).filter(Boolean);
  const shown = covers.slice(0, STRIP);
  const more = Math.max(0, (summary?.count ?? 0) - shown.length);

  return (
    <section className="just-played" data-testid="just-played-card" aria-labelledby={headingId}>
      <header className="just-played-header">
        <h2 className="just-played-title" id={headingId}>
          {t('stream.justPlayed.title', { title: played.songTitle })}
        </h2>
        <button type="button" className="just-played-close" onClick={onDismiss} aria-label={t('stream.justPlayed.close')} data-testid="just-played-close">
          <FaTimes aria-hidden="true" />
        </button>
      </header>
      {jumped && played.memoryId != null ? (
        <MemoryClipPlayer memoryId={played.memoryId} title={played.songTitle} onGoPremium={onGoPremium} />
      ) : (
        <button type="button" className="just-played-jump" onClick={() => setJumped(true)} data-testid="just-played-jump">
          {!premium && <FaLock aria-hidden="true" />}
          {t('stream.justPlayed.jump')}
        </button>
      )}

      {!premium && (
        <div className="just-played-premium" data-testid="just-played-premium">
          <span className="just-played-brand">{t('stream.justPlayed.premium')}</span>
          {summary?.displayName && <span className="just-played-name">{summary.displayName}</span>}
          {shown.length > 0 && (
            <div className="just-played-strip" aria-hidden="true">
              {shown.map((src) => <img key={src} src={src} alt="" />)}
              {more > 0 && <span className="just-played-more">{t('stream.justPlayed.more', { count: more, formattedCount: format.number(more) })}</span>}
            </div>
          )}
          <p className="just-played-pitch">
            {t('stream.justPlayed.pitch', { count: summary?.count ?? 0, formattedCount: format.number(summary?.count ?? 0) })}
          </p>
          <button type="button" className="just-played-go" onClick={onGoPremium} data-testid="just-played-go">
            {t('stream.justPlayed.goPremium')}
          </button>
        </div>
      )}
    </section>
  );
};

export default JustPlayedCard;
